import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import "./index.css";
import cn from "classnames";
import {useImages} from "../../contexts/images";
import {useSetting} from "../../contexts/setting";
import {
  PORTRAIT,
  ZOOMIN,
  ZOOMOUT,
  TRASH_BIN,
  ROTATE_L,
  ROTATE_R,
  FLIP_H,
  FLIP_V,
} from "../../data/constants";
import BiggerPicture from "libs/BiggerPicture";
import {checkIfAnimated, fileToDataUrl, getSizeFromBase64, isSvgFile} from "libs/bg-utils";
import {calculateCoverScale} from "libs/utils";
import {Slider} from "biggerui";
import {track} from "libs/analytics";
import {API} from "../../api.js";
import {openErrorDialog} from "../../layout/ErrorDialog";
import {UPLOAD_MAX_BYTES} from "../../data/constants";

const ImageUpload = ({
  className = undefined,
  id,
  isLogo = false,
  transformKey = undefined,
  emptyLogo = false,
  imageKey = undefined,
  imageSlotKey = undefined,
  isMini = false,
  coverFitKey = undefined,
  alwaysVisible = false,
}) => {
  const [orientation, setOrientation] = useState("horiz");
  const apiRef = useRef(null);
  const wrapperRef = useRef(null);
  const classSizes = {
    [orientation === "horiz" ? "height" : "width"]: "100%",
  };
  const setTransformRef = useRef(null);
  // Track the last version we auto-scaled for, to detect new uploads across all components
  const lastScaledVersionRef = useRef(-1);
  // A caller can request a new fit when its upload area changes shape.
  const lastCoverFitKeyRef = useRef(coverFitKey);
  const {sceneId: savedSceneId} = useParams();
  const {
    logos,
    editLogos,
    editLogoTransform,
    editLogoX,
    editLogoY,
    editLogoScale,
    logoX,
    logoY,
    logoScale,
    uploadImages,
    editUploadImages,
    uploadX,
    uploadY,
    uploadScale,
    editUploadTransform,
    removeUploadImages,
    editUploadX,
    editUploadY,
    editUploadScale,
    editUploadFlip,
    editUploadRotate,
    uploadRotate,
    uploadFlip,
    editIsUploadAnimation,
    isUploadAnimation,
    uploadImageVersion: contextUploadImageVersion,
    uploadImageFile,
    getUploadImageVersion,
    incPendingUpload,
    decPendingUpload,
  } = useImages();
  const {
    isDropzoneVisible,
    isImageToolsExpanded,
    currentImageToolsId,
    editIsBgToolsExpanded,
    editIsImageToolsExpanded,
    editCurrentImageToolsId,
    locked,
    currentVideoPopup,
    hash,
  } = useSetting();

  const uploadImageVersion = getUploadImageVersion ? getUploadImageVersion(imageSlotKey || transformKey || id) : contextUploadImageVersion;
  const isVideoPopup = currentVideoPopup === id;
  // The toolbar's Dropzone toggle is channel-and-layout scoped, but the flag it
  // writes is global. A skin whose design owns the upload zone — and therefore
  // offers no toggle — asks for `alwaysVisible`, so a flag another product left
  // off cannot hide a dropzone the user has no control to bring back.
  const outerClass = cn("image-upload-outer", {hidden: !isDropzoneVisible && !alwaysVisible});
  // Ensure transform key is defined before checks below
  const keyForTransform = transformKey || id;
  const isOpenForThis = isImageToolsExpanded && currentImageToolsId === keyForTransform;
  const buttonPack = cn("button-pack", {visible: isOpenForThis});
  const expandBtn = cn("round-btn expander", {opaque: isOpenForThis, hidden: isVideoPopup});
  const wrapperClass = cn("image-wrapper", {active: isOpenForThis});
  const isLocked =
    locked.some((elem) => elem === id) || (typeof id === "string" && id.startsWith("custom-qr"));
  // Check if the parent template is locked (locked array has template IDs, transformKey starts with template ID)
  const isTemplateLocked =
    transformKey && locked.some((templateId) => transformKey.startsWith(templateId));

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  // Last committed (non-blob) value for this slot. Read by the upload
  // failure path to revert without race risk — closure capture would,
  // under concurrent picks, snapshot a `blob:` URL that the other in-flight
  // upload's success may have already revoked. Populated by a sync effect
  // below (runs after `uploadedImage` is derived).
  const lastCommittedRef = useRef("");

  const acceptedTypes = [
    // Images
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/avif",
    "image/svg+xml",
    "image/bmp",
    "image/x-icon",
    "image/tiff",
    "image/heic",
    "image/heif",
    // Videos
    "video/mp4",
    "video/webm",
    "video/ogg",
    "video/quicktime",
    "video/x-m4v",
    "video/x-matroska",
    "video/x-msvideo",
    "video/x-ms-wmv",
    "video/x-flv",
    "video/3gpp",
    "video/3gpp2",
  ];

  const currentCoords = () => {
    if (transformKey) {
      return {
        x: uploadX?.[transformKey] ?? 0,
        y: uploadY?.[transformKey] ?? 0,
        scale: uploadScale?.[transformKey] ?? 1,
        rotate: uploadRotate?.[transformKey] ?? 0,
        flip: uploadFlip?.[transformKey] ?? {x: false, y: false},
      };
    } else if (isLogo) {
      return {
        x: logoX?.[id] ?? 180,
        y: logoY?.[id] ?? 160,
        scale: logoScale?.[id] ?? 0.9,
        rotate: 0,
        flip: {x: false, y: false},
      };
    } else
      return {
        x: uploadX?.[id] ?? uploadX?.main ?? 0,
        y: uploadY?.[id] ?? uploadY?.main ?? 0,
        scale: uploadScale?.[id] ?? uploadScale?.main ?? 1,
        rotate: uploadRotate?.[id] ?? uploadRotate?.main ?? 0,
        flip: uploadFlip?.[id] ?? uploadFlip?.main ?? {x: false, y: false},
      };
  };

  // console.log("currentCoords", currentCoords());

  const {x, y, scale, rotate, flip} = currentCoords();
  const isSwap = rotate === 90 || rotate === 270;

  const handleFlip = (flip) => {
    editUploadFlip(keyForTransform, flip);
  };

  const handleRotate = (rotate) => {
    editUploadRotate(keyForTransform, rotate);
  };

  const onImageRemove = () => {
    if (isLogo) {
      editLogos("main", "");
    } else if (imageSlotKey) {
      removeUploadImages(imageSlotKey, true);
    } else if (isTemplateLocked && imageKey) {
      // Locked template: only remove the per-template override, keep the shared image
      editUploadImages(keyForTransform, null, true);
    } else {
      removeUploadImages(imageKey || id, isLocked || !!imageKey);
    }
  };

  const expand = () => {
    // Toggle exclusively for this image id
    const toolsKey = keyForTransform;
    if (isOpenForThis) {
      // close if already open for this id
      editIsImageToolsExpanded(false);
      editCurrentImageToolsId(null);
    } else {
      // open and focus this id
      editCurrentImageToolsId(toolsKey);
      editIsImageToolsExpanded(true);
    }
    // ensure BG tools are closed when opening image tools
    editIsBgToolsExpanded(false);
  };
  const handleTransform = (x, y, scale) => {
    if (isOpenForThis) {
      if (isLogo) {
        console.log("LOGO DETECTED", x, y, scale);
        editLogoX(id, x);
        editLogoY(id, y);
        editLogoScale(id, scale);
      } else {
        editUploadX(keyForTransform, x, true);
        editUploadY(keyForTransform, y, true);
        editUploadScale(keyForTransform, scale, true);
      }
    }
  };

  // Single setter that routes to the right context slot based on which
  // ImageUpload instance this is. Used both for the optimistic local-blob
  // preview and for the final CDN-URL commit, so the three branches don't
  // diverge in how they write scene state.
  const writeImageSlot = (value) => {
    if (isLogo) {
      editLogos(id, value);
    } else if (imageSlotKey) {
      editUploadImages(imageSlotKey, value, true);
    } else if (isTemplateLocked && imageKey) {
      // Locked template: store as per-template override, don't touch shared image
      editUploadImages(keyForTransform, value, true);
    } else {
      editUploadImages(imageKey || id, value, isLocked || !!imageKey);
    }
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    if (uploadImageFile) {await uploadImageFile(file, imageSlotKey || transformKey || imageKey || id, isLogo); return;}

    track("neurobaker.image.uploaded", {
      type: file.type,
      size: file.size,
      isLogo,
    });

    // SVG: store inline as a data URL instead of uploading to S3. The
    // /api/v3/uploads allowlist rejects image/svg+xml (it can carry script),
    // and SVG renders fine via <img src> (sandboxed — no scripts, no external
    // loads), so there's no need to round-trip it through the gallery. This
    // restores the pre-#169 behavior for SVG (the upload-first refactor swapped
    // the FileReader→data-URL path for an S3 upload that 400s on SVG) and
    // mirrors BgSelector's custom-bg SVG branch. No blob preview /
    // pendingUpload dance — the value is final the moment the read resolves.
    if (isSvgFile(file)) {
      try {
        writeImageSlot(await fileToDataUrl(file));
        editCurrentImageToolsId(keyForTransform);
        editIsImageToolsExpanded(true);
        editIsBgToolsExpanded(false);
        if (!isLogo) {
          editIsUploadAnimation(await checkIfAnimated(file));
        }
      } catch (err) {
        console.error("[ImageUpload] SVG read failed", err);
        openErrorDialog({
          title: isLogo ? "Couldn't load SVG logo" : "Couldn't load SVG",
          summary: "The SVG file couldn't be read. Try a different file.",
        });
      }
      return;
    }

    // Upload-first (#169). Scene state is URL-shaped from the moment of
    // drop: blob: URL for instant preview, swap to CDN URL when the upload
    // resolves. `pendingUploadCount` blocks scene save in the meantime so
    // a `blob:` URL — which 404s outside the originating tab — can't be
    // persisted into the scene blob.
    const localUrl = URL.createObjectURL(file);
    writeImageSlot(localUrl);
    // Auto-open transform controls so the user can position immediately,
    // same as the previous behavior.
    editCurrentImageToolsId(keyForTransform);
    editIsImageToolsExpanded(true);
    editIsBgToolsExpanded(false);

    incPendingUpload();
    try {
      const result = await API.uploadFile(file);
      if (!result?.url) {
        throw new Error("Upload API returned no URL");
      }
      writeImageSlot(result.url);
      // Animation check is irrelevant for static logos; keep it on the
      // upload branch where the BG plumbing watches `isUploadAnimation`.
      if (!isLogo) {
        const isAnimated = await checkIfAnimated(file);
        editIsUploadAnimation(isAnimated);
      }
    } catch (err) {
      console.error("[ImageUpload] upload failed", err);
      // Revert to the last *committed* (non-blob) value via ref — closure
      // capture would risk reverting to a blob URL that the other
      // in-flight upload's success has already revoked.
      writeImageSlot(lastCommittedRef.current);
      openErrorDialog({
        title: isLogo ? "Couldn't upload logo" : "Couldn't upload image",
        summary:
          err?.envelope?.error?.isLikelyCors || err?.name === "NetworkError"
            ? "The upload didn't reach the server. Check your connection and try again."
            : err?.status === 413
              ? `The file is too large to upload. Try a smaller one (under ${UPLOAD_MAX_BYTES / (1024 * 1024)} MB).`
              : err?.status === 400 || err?.status === 415
                ? "Unsupported file type. Use PNG, JPEG, WebP, GIF, MP4, WebM, MOV, or MKV."
                : `Upload failed${err?.message ? `: ${err.message}` : ""}.`,
        diagnostics: err?.envelope,
      });
    } finally {
      URL.revokeObjectURL(localUrl);
      decPendingUpload();
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    (async () => {
      const isAnimated = await checkIfAnimated(uploadImages.main);
      console.log("Is upload image animated:", isAnimated);
      editIsUploadAnimation(isAnimated);
    })();
  }, [uploadImages, editIsUploadAnimation]);

  // Per-template override (null = explicitly removed, undefined = no override)
  const perTemplateOverride =
    imageKey && keyForTransform ? uploadImages[keyForTransform] : undefined;

  // Clean up per-template overrides and recalculate fit when template is unlocked
  useEffect(() => {
    if (!isTemplateLocked && perTemplateOverride !== undefined) {
      editUploadImages(keyForTransform, undefined, true);
      // Force recalculation of cover scale for the shared image
      lastScaledVersionRef.current = -1;
    }
  }, [isTemplateLocked, perTemplateOverride, keyForTransform, editUploadImages]);

  const uploadedImage = imageSlotKey
    ? uploadImages[imageSlotKey]
    : perTemplateOverride !== undefined && isTemplateLocked
      ? perTemplateOverride
      : imageKey
        ? uploadImages[imageKey]
        : isLocked
          ? uploadImages[id]
          : uploadImages?.main;

  // Sync `lastCommittedRef` to the current slot value whenever it changes
  // to a non-blob value. Blob URLs are transient previews and should never
  // become the revert target.
  const currentSlotValue = isLogo ? logos?.main || "" : uploadedImage || "";
  useEffect(() => {
    if (typeof currentSlotValue === "string" && !currentSlotValue.startsWith("blob:")) {
      lastCommittedRef.current = currentSlotValue;
    }
  }, [currentSlotValue]);

  // Calculate a centered cover fit after an upload or an explicit layout change.
  useEffect(() => {
    if (isLogo || !uploadedImage || !wrapperRef.current) return;
    const uploadChanged = uploadImageVersion !== lastScaledVersionRef.current;
    const coverFitKeyChanged =
      coverFitKey !== undefined && coverFitKey !== lastCoverFitKeyRef.current;
    if (!uploadChanged && !coverFitKeyChanged) return;

    // On initial load with a saved scene (version 0), preserve existing transforms
    if (!coverFitKeyChanged && uploadImageVersion === 0 && (savedSceneId || hash)) {
      lastScaledVersionRef.current = 0;
      return;
    }

    lastScaledVersionRef.current = uploadImageVersion;
    lastCoverFitKeyRef.current = coverFitKey;
    let cancelled = false;

    (async () => {
      try {
        const {width, height} = await getSizeFromBase64(uploadedImage);
        if (cancelled || !width || !height || width <= 0 || height <= 0) return;

        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const containerWidth = wrapper.offsetWidth;
        const containerHeight = wrapper.offsetHeight;
        if (!containerWidth || !containerHeight) return;

        const coverScale = calculateCoverScale(containerWidth, containerHeight, width, height);
        const centerX = (containerWidth - width * coverScale) / 2;
        const centerY = (containerHeight - height * coverScale) / 2;

        editUploadScale(keyForTransform, coverScale, true);
        editUploadX(keyForTransform, centerX, true);
        editUploadY(keyForTransform, centerY, true);
      } catch (error) {
        console.error("Error calculating cover scale for uploaded image:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    uploadedImage,
    uploadImageVersion,
    coverFitKey,
    isLogo,
    keyForTransform,
    editUploadScale,
    editUploadX,
    editUploadY,
    savedSceneId,
    hash,
  ]);

  return (
    <div className={outerClass} ref={wrapperRef}>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(",")}
        onChange={handleInputChange}
        style={{display: "none"}}
      />
      <div className={cn("image-upload", className)}>
        {(isLogo ? !logos.main && !emptyLogo : !uploadedImage) && (
          <button
            className={cn("dropzone", {dragging: isDragging, mini: isMini})}
            onClick={openFileDialog}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            Click or Drop
            <br />
            {isLogo ? "Logo" : "Image/Video"} here
          </button>
        )}
        {(isLogo ? logos.main : uploadedImage) && (
          <div className="picture">
            <>
              <BiggerPicture
                ref={apiRef}
                src={isLogo ? logos.main : uploadedImage}
                wrapperClassName={wrapperClass}
                imgClassName="upld-image"
                imgStyle={{}}
                wrapperStyle={{pointerEvents: isOpenForThis ? "auto" : "none"}}
                x={x}
                y={y}
                scale={scale}
                rotate={rotate}
                flip={flip}
                minScale={0.1}
                maxScale={10}
                pinchSpeed={0.0024}
                onTransform={handleTransform}
              />
              <div className="tools">
                <button title="Edit portrait" className={expandBtn} onClick={() => expand()}>
                  <img src={PORTRAIT} />
                </button>
                <div className={buttonPack}>
                  <button className="round-btn" onClick={() => apiRef.current?.zoomIn(1)}>
                    <img src={ZOOMIN} />
                  </button>
                  <button className="round-btn" onClick={() => apiRef.current?.zoomOut(1)}>
                    <img src={ZOOMOUT} />
                  </button>

                  {/*<button
                    className="round-btn"
                    onClick={() => handleRotate((rotate + 360 - 90) % 360)}
                  >
                    <img src={ROTATE_L} />
                  </button>
                  <button
                    className="round-btn"
                    onClick={() => handleRotate((rotate + 90) % 360)}
                  >
                    <img src={ROTATE_R} />
                  </button>*/}

                  <div className="rotate-container">
                    <div className="rotate-buttons">
                      <button className="rotate-btn" onClick={() => handleRotate(90)}>
                        90º
                      </button>
                      <button className="rotate-btn" onClick={() => handleRotate(180)}>
                        180º
                      </button>
                      <button className="rotate-btn" onClick={() => handleRotate(270)}>
                        270º
                      </button>
                    </div>
                    <Slider
                      id="bg-rotate"
                      min="0"
                      max="360"
                      value={uploadRotate?.[keyForTransform] ?? 0}
                      onChange={(e) => handleRotate(Number(e.target.value))}
                      className="rotate-slider"
                    />
                  </div>
                  <button
                    className="round-btn"
                    onClick={() => {
                      isSwap
                        ? handleFlip({...flip, y: !flip.y})
                        : handleFlip({...flip, x: !flip.x});
                    }}
                  >
                    <img src={FLIP_H} />
                  </button>
                  <button
                    className="round-btn"
                    onClick={() => {
                      isSwap
                        ? handleFlip({...flip, x: !flip.x})
                        : handleFlip({...flip, y: !flip.y});
                    }}
                  >
                    <img src={FLIP_V} />
                  </button>
                  {/*<button className="round-btn" onClick={() => resetTransform()}>*/}
                  {/*  <img src={RESET} />*/}
                  {/*</button>*/}
                  <button className="round-btn" onClick={onImageRemove}>
                    <img src={TRASH_BIN} />
                  </button>
                </div>
              </div>
            </>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
